import React from "react";
import { Space, Tag, Tooltip, Button } from "antd";
import { Typography } from "antd";
import {
  FolderOutlined,
  FolderOpenOutlined,
  FileTextOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;

interface FinanceTreeItem {
  id: number;
  serialNumber: string;
  name: string;
  nameAr: string;
  slug: string;
  editable: boolean;
  parentId?: number | null;
  level: number;
  balance: number;
  totalCredit: number;
  totalDebit: number;
  createdAt: string;
  updatedAt: string;
  children?: FinanceTreeItem[];
}

interface FinanceTreeNodeProps {
  item: FinanceTreeItem;
  currentLang: string;
  hasChildren: boolean;
  onAdd: (item: FinanceTreeItem) => void;
  onEdit: (item: FinanceTreeItem) => void;
  onDelete: (item: FinanceTreeItem) => void;
  t: any;
}

const FinanceTreeNode: React.FC<FinanceTreeNodeProps> = ({
  item,
  currentLang,
  hasChildren,
  onAdd,
  onEdit,
  onDelete,
  t,
}) => {
  const navigate = useNavigate();
  const title = currentLang === "ar" ? item.nameAr : item.name;

  const handleViewTransactionLines = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/finance/tree-transaction-lines?financeTreeId=${item.id}&name=${encodeURIComponent(title)}`);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        borderRadius: "10px",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        minHeight: "64px",
      }}
      className="finance-tree-node"
    >
      <Space size="middle" style={{ flex: 1 }}>
        {hasChildren ? (
          <div
            className="finance-tree-icon"
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #ffd666 0%, #faad14 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(250, 173, 20, 0.25)",
            }}
          >
            <FolderOutlined style={{ fontSize: "20px", color: "#fff" }} />
          </div>
        ) : (
          <div
            className="finance-tree-icon"
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #69b1ff 0%, #1890ff 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(24, 144, 255, 0.25)",
            }}
          >
            <FileTextOutlined style={{ fontSize: "18px", color: "#fff" }} />
          </div>
        )}
        <Space direction="vertical" size={2}>
          <Text strong style={{ fontSize: "15px", color: "#262626" }}>
            {title}
          </Text>
          <Text type="secondary" style={{ fontSize: "13px", fontWeight: 500 }}>
            {item.serialNumber}
          </Text>
        </Space>
      </Space>
      <Space size="middle" style={{ marginLeft: "auto" }}>
        <Space size="small" wrap>
          <Tag
            color="blue"
            style={{
              borderRadius: "6px",
              padding: "2px 10px",
              fontWeight: 600,
              border: "none",
              fontSize: "12px",
            }}
          >
            Level {item.level}
          </Tag>
          <Tooltip title={t("label.total_debit")}>
            <Tag
              color="orange"
              style={{
                borderRadius: "6px",
                padding: "4px 12px",
                fontWeight: 600,
                border: "none",
                fontSize: "12px",
                minWidth: "100px",
                textAlign: "center",
              }}
            >
              {t("label.total_debit")}: {item.totalDebit.toLocaleString()}{" "}
              {t("label.currency")}
            </Tag>
          </Tooltip>
          <Tooltip title={t("label.total_credit")}>
            <Tag
              color="cyan"
              style={{
                borderRadius: "6px",
                padding: "4px 12px",
                fontWeight: 600,
                border: "none",
                fontSize: "12px",
                minWidth: "100px",
                textAlign: "center",
              }}
            >
              {t("label.total_credit")}: {item.totalCredit.toLocaleString()}{" "}
              {t("label.currency")}
            </Tag>
          </Tooltip>
          <Tooltip title={t("label.balance")}>
            <Tag
              color={item.balance >= 0 ? "success" : "error"}
              style={{
                borderRadius: "6px",
                padding: "4px 12px",
                fontWeight: 600,
                border: "none",
                fontSize: "12px",
                minWidth: "100px",
                textAlign: "center",
              }}
            >
              {t("label.balance")}: {item.balance >= 0 ? "+" : ""}
              {item.balance.toLocaleString()} {t("label.currency")}
            </Tag>
          </Tooltip>
          {item.editable ? (
            <Tag
              color="green"
              style={{
                borderRadius: "6px",
                padding: "2px 10px",
                fontWeight: 600,
                border: "none",
                fontSize: "12px",
              }}
            >
              ✓ Editable
            </Tag>
          ) : (
            <Tag
              color="default"
              style={{
                borderRadius: "6px",
                padding: "2px 10px",
                fontWeight: 600,
                fontSize: "12px",
              }}
            >
              🔒 Read-only
            </Tag>
          )}
        </Space>
        <Tooltip title={t("button.view_transaction_lines")}>
          <Button
            type="default"
            size="small"
            icon={<UnorderedListOutlined />}
            onClick={handleViewTransactionLines}
            style={{
              background: "linear-gradient(135deg, #faad14 0%, #ffc53d 100%)",
              border: "none",
              borderRadius: "6px",
              boxShadow: "0 2px 6px rgba(250, 173, 20, 0.3)",
              color: "#fff",
            }}
          />
        </Tooltip>
        <Space size={4}>
          <Tooltip title={t("button.add")}>
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onAdd(item);
              }}
              style={{
                background: "linear-gradient(135deg, #52c41a 0%, #73d13d 100%)",
                border: "none",
                borderRadius: "6px",
                boxShadow: "0 2px 6px rgba(82, 196, 26, 0.3)",
              }}
            />
          </Tooltip>
          {item.editable && (
            <>
              <Tooltip title={t("button.edit")}>
                <Button
                  type="primary"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(item);
                  }}
                  style={{
                    background: "linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)",
                    border: "none",
                    borderRadius: "6px",
                    boxShadow: "0 2px 6px rgba(24, 144, 255, 0.3)",
                  }}
                />
              </Tooltip>
              <Tooltip title={t("button.delete")}>
                <Button
                  type="primary"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(item);
                  }}
                  style={{
                    background: "linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)",
                    border: "none",
                    borderRadius: "6px",
                    boxShadow: "0 2px 6px rgba(255, 77, 79, 0.3)",
                  }}
                />
              </Tooltip>
            </>
          )}
        </Space>
      </Space>
    </div>
  );
};

export default FinanceTreeNode;

